import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTER_ASC_ORDERS } from 'app/AppRouter';
import {
  getAscOrdersEnabled,
  getClaimsEnabled,
  getDepositReturnListEnabled,
  getExadisReturnListEnabled,
} from 'domains/appContext/AppContext.store';
import { fetchDepositReturnListRequestSaga, fetchExadisReturnListRequestSaga } from 'domains/order/Order.store';
import AscOrdersTable from 'pages/MyOrdersPage/AscOrders/Tables/AscOrdersTable';
import DepositReturnList from 'pages/MyOrdersPage/DepositReturnList/DepositReturnList';
import ExadisReturnList from 'pages/MyOrdersPage/ExadisReturnList/ExadisReturnList';
import OrderClaims from 'pages/MyOrdersPage/OrderClaims';
import { Container, Flex, MarginBox, Tab, TabItems, Tabs, Text } from 'UI';
import { getCondArrayItem } from 'utils';

export const ASC_ORDERS_TAB = 'asc-orders';
export const CLAIMS_TAB = 'claims';
export const EXADIS_RETURN_LIST_TAB = 'exadis-return-list';
export const DEPOSIT_RETURN_LIST_TAB = 'deposit-return-list';
export const TAB_PARAM = 'tab';

type AscOrdersTab =
  | typeof ASC_ORDERS_TAB
  | typeof CLAIMS_TAB
  | typeof EXADIS_RETURN_LIST_TAB
  | typeof DEPOSIT_RETURN_LIST_TAB;

const resolveActiveTab = (
  tabParam: string | null,
  ascEnabled: boolean,
  claimsEnabled: boolean,
  exadisReturnListEnabled: boolean,
  depositReturnListEnabled: boolean,
): AscOrdersTab => {
  if (tabParam === EXADIS_RETURN_LIST_TAB && exadisReturnListEnabled && claimsEnabled && ascEnabled)
    return EXADIS_RETURN_LIST_TAB;
  if (tabParam === DEPOSIT_RETURN_LIST_TAB && depositReturnListEnabled && claimsEnabled && ascEnabled)
    return DEPOSIT_RETURN_LIST_TAB;
  if (tabParam === CLAIMS_TAB && claimsEnabled) return CLAIMS_TAB;
  if (tabParam === ASC_ORDERS_TAB && ascEnabled) return ASC_ORDERS_TAB;
  if (ascEnabled) return ASC_ORDERS_TAB;
  if (claimsEnabled) return CLAIMS_TAB;
  return ASC_ORDERS_TAB;
};

const AscOrdersPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const ascOrdersEnabled = useSelector(getAscOrdersEnabled) ?? true;
  const claimsEnabled = useSelector(getClaimsEnabled) ?? true;
  const exadisReturnListEnabled = useSelector(getExadisReturnListEnabled);
  const depositReturnListEnabled = useSelector(getDepositReturnListEnabled);

  useEffect(() => {
    if (ascOrdersEnabled && claimsEnabled && exadisReturnListEnabled) {
      dispatch(fetchExadisReturnListRequestSaga());
    }
  }, [dispatch, ascOrdersEnabled, claimsEnabled, exadisReturnListEnabled]);

  useEffect(() => {
    if (ascOrdersEnabled && claimsEnabled && depositReturnListEnabled) {
      dispatch(fetchDepositReturnListRequestSaga());
    }
  }, [dispatch, ascOrdersEnabled, claimsEnabled, depositReturnListEnabled]);

  const tabParam = new URLSearchParams(search).get(TAB_PARAM);
  const activeTab = resolveActiveTab(
    tabParam,
    ascOrdersEnabled,
    claimsEnabled,
    exadisReturnListEnabled,
    depositReturnListEnabled,
  );

  const handleTabChange = (key: string) => {
    history.replace(`${ROUTER_ASC_ORDERS}?${TAB_PARAM}=${key}`);
  };

  const tabItems: TabItems[] = [
    ...getCondArrayItem(
      ascOrdersEnabled && {
        key: ASC_ORDERS_TAB,
        label: t('order.order_detail.asc_orders', 'My ASC orders'),
        component: <AscOrdersTable />,
      },
    ),
    ...getCondArrayItem(
      claimsEnabled && {
        key: CLAIMS_TAB,
        label: t('order.order_detail.claims', 'Claims'),
        component: <OrderClaims />,
      },
    ),
    ...getCondArrayItem(
      exadisReturnListEnabled &&
        claimsEnabled &&
        ascOrdersEnabled && {
          key: EXADIS_RETURN_LIST_TAB,
          label: t('order.order_detail.exadis_return_list', 'Return List'),
          component: <ExadisReturnList />,
        },
    ),
    ...getCondArrayItem(
      depositReturnListEnabled &&
        claimsEnabled &&
        ascOrdersEnabled && {
          key: DEPOSIT_RETURN_LIST_TAB,
          label: t('order.order_detail.deposit_return_list', 'Deposit Return'),
          component: <DepositReturnList />,
        },
    ),
  ];

  return (
    <Container>
      <MarginBox mt={25}>
        <Flex direction={'column'}>
          <Text type={'h1_banner_light'}>{t('order.asc_orders_page.title', 'My ASC Orders')}</Text>
          <Tabs activeKey={activeTab} onChange={handleTabChange} tabBarGutter={15}>
            {tabItems?.map((item) => (
              <Tab tab={item.label} key={item.key}>
                {item.component}
              </Tab>
            ))}
          </Tabs>
        </Flex>
      </MarginBox>
    </Container>
  );
};

export default AscOrdersPage;
